import { useState, useEffect } from 'react'
import {
  Sparkles,
  Bot,
  Plus,
  Search,
  TrendingUp,
  Activity,
  Users,
  Grid,
  List,
  Filter,
  X
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { FluentCard } from '@/components/layout/page-header'
import { MyAgentsTab } from './MyAgentsTab'
import { Link } from 'react-router-dom'
import { useAgentStats } from '@/lib/hooks/use-ai-studio'
import { cn } from '@/lib/utils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Badge } from '@/components/ui/badge'
import type { DeploymentStatus } from '@/lib/api/services/aiStudio'

// Types
type ViewMode = 'grid' | 'table'
type StatusFilter = 'all' | DeploymentStatus
type TypeFilter = 'all' | 'custom' | 'preset'

// Constants
const LOCAL_STORAGE_VIEW_KEY = 'ai-agents-view-mode'

interface StatsCardProps {
  icon: React.ElementType
  label: string
  value: string | number
  trend?: string
  trendUp?: boolean
  color: string
  loading?: boolean
}

function StatsCard({ icon: Icon, label, value, trend, trendUp, color, loading }: StatsCardProps) {
  return (
    <FluentCard className="relative overflow-hidden">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
          {loading ? (
            <div className="h-8 w-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mt-1" />
          ) : (
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{value}</p>
          )}
          {trend && !loading && (
            <div className={`flex items-center gap-1 mt-2 text-sm ${trendUp ? 'text-emerald-600' : 'text-red-500'}`}>
              <TrendingUp className={`h-3 w-3 ${!trendUp && 'rotate-180'}`} />
              {trend}
            </div>
          )}
        </div>
        <div className={`p-3 rounded-xl ${color}`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
      </div>
    </FluentCard>
  )
}

// Format large numbers (e.g., 24500 -> "24.5K")
function formatNumber(num: number): string {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`
  }
  return num.toString()
}

// Format milliseconds to seconds with 1 decimal
function formatResponseTime(ms: number): string {
  return `${(ms / 1000).toFixed(1)}s`
}

export default function AgentsPage() {
  const [searchQuery, setSearchQuery] = useState('')

  // View mode with localStorage persistence
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    if (typeof window === 'undefined') return 'grid'
    const saved = localStorage.getItem(LOCAL_STORAGE_VIEW_KEY)
    return (saved === 'table' ? 'table' : 'grid') as ViewMode
  })

  // Filters
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all')

  // Pagination
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(12)
  const [totalCount, setTotalCount] = useState(0)

  // Fetch stats from API
  const { data: stats, isLoading: statsLoading } = useAgentStats()

  // Persist view mode to localStorage
  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_VIEW_KEY, viewMode)
  }, [viewMode])

  // Reset page when filters change
  useEffect(() => {
    setPage(1)
  }, [searchQuery, statusFilter, typeFilter])

  // Check if any filters are active
  const hasActiveFilters = statusFilter !== 'all' || typeFilter !== 'all'

  // Clear all filters
  const clearFilters = () => {
    setStatusFilter('all')
    setTypeFilter('all')
    setSearchQuery('')
  }

  return (
    <div className="space-y-6">
      {/* Hero Section */}
      <div className="relative overflow-hidden rounded-none bg-gradient-to-r from-violet-500 via-purple-500 to-fuchsia-500 p-8 lg:p-10">
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

        <div className="relative z-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="p-4 rounded-2xl bg-white/20 backdrop-blur-sm">
              <Sparkles className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold text-white mb-1">AI Agents</h1>
              <p className="text-white/80">Build, test, and deploy custom AI agents</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/ai-studio/builder">
              <Button className="bg-white text-purple-600 hover:bg-white/90 rounded-full px-6 h-11">
                <Plus className="mr-2 h-4 w-4" />
                New Agent
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatsCard
          icon={Bot}
          label="Active Agents"
          value={stats?.activeAgents ?? 0}
          trend={stats?.agentsTrend}
          trendUp={stats?.agentsTrend?.startsWith('+')}
          color="bg-gradient-to-br from-violet-400 to-purple-600"
          loading={statsLoading}
        />
        <StatsCard
          icon={Activity}
          label="Total Requests"
          value={stats ? formatNumber(stats.totalRequests) : '0'}
          trend={stats?.requestsTrend}
          trendUp={stats?.requestsTrend?.startsWith('+')}
          color="bg-gradient-to-br from-emerald-400 to-green-600"
          loading={statsLoading}
        />
        <StatsCard
          icon={Users}
          label="Active Users"
          value={stats?.activeUsers ?? 0}
          trend={stats?.usersTrend}
          trendUp={stats?.usersTrend?.startsWith('+')}
          color="bg-gradient-to-br from-blue-400 to-cyan-600"
          loading={statsLoading}
        />
        <StatsCard
          icon={TrendingUp}
          label="Avg Response Time"
          value={stats ? formatResponseTime(stats.avgResponseTimeMs) : '0s'}
          trend={stats?.responseTimeTrend}
          trendUp={stats?.responseTimeTrend?.startsWith('-')}
          color="bg-gradient-to-br from-amber-400 to-orange-600"
          loading={statsLoading}
        />
      </div>

      {/* Main Content */}
      <FluentCard className="p-0">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Bot className="h-5 w-5 text-purple-600" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">My Agents</h2>
            {totalCount > 0 && (
              <Badge variant="secondary" className="ml-2">
                {totalCount}
              </Badge>
            )}
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search agents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 w-full lg:w-64 rounded-xl border-gray-200 dark:border-gray-700"
              />
            </div>

            {/* Filters Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className={cn(
                    "rounded-xl gap-2",
                    hasActiveFilters && "border-purple-500 text-purple-600"
                  )}
                >
                  <Filter className="h-4 w-4" />
                  Filters
                  {hasActiveFilters && (
                    <Badge className="ml-1 h-5 w-5 p-0 flex items-center justify-center bg-purple-600 text-white">
                      {(statusFilter !== 'all' ? 1 : 0) + (typeFilter !== 'all' ? 1 : 0)}
                    </Badge>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Status</DropdownMenuLabel>
                <DropdownMenuRadioGroup value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
                  <DropdownMenuRadioItem value="all">All Statuses</DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="Active">Active</DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="Draft">Draft</DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="Testing">Testing</DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="Deprecated">Deprecated</DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Type</DropdownMenuLabel>
                <DropdownMenuRadioGroup value={typeFilter} onValueChange={(v) => setTypeFilter(v as TypeFilter)}>
                  <DropdownMenuRadioItem value="all">All Types</DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="custom">Custom</DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="preset">Preset</DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
                {hasActiveFilters && (
                  <>
                    <DropdownMenuSeparator />
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={clearFilters}
                    >
                      <X className="h-4 w-4 mr-2" />
                      Clear Filters
                    </Button>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* View Mode Toggle */}
            <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={cn(
                  'p-2 rounded-md transition-colors',
                  viewMode === 'grid'
                    ? 'bg-white dark:bg-gray-700 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                )}
                title="Grid view"
              >
                <Grid className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={cn(
                  'p-2 rounded-md transition-colors',
                  viewMode === 'table'
                    ? 'bg-white dark:bg-gray-700 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                )}
                title="Table view"
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <div className="flex items-center gap-2 px-6 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
            <span className="text-sm text-gray-500">Active filters:</span>
            {statusFilter !== 'all' && (
              <Badge
                variant="secondary"
                className="gap-1 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600"
                onClick={() => setStatusFilter('all')}
              >
                Status: {statusFilter}
                <X className="h-3 w-3" />
              </Badge>
            )}
            {typeFilter !== 'all' && (
              <Badge
                variant="secondary"
                className="gap-1 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600"
                onClick={() => setTypeFilter('all')}
              >
                Type: {typeFilter}
                <X className="h-3 w-3" />
              </Badge>
            )}
          </div>
        )}

        <div className="p-6">
          <MyAgentsTab
            searchQuery={searchQuery}
            viewMode={viewMode}
            statusFilter={statusFilter}
            typeFilter={typeFilter}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            onTotalCountChange={setTotalCount}
          />
        </div>
      </FluentCard>
    </div>
  )
}
